import axios, { AxiosResponse } from 'axios';
import { Configuration, PostConfiguration } from 'data/configuration';
import { AgentStates } from 'types';
import { ClaimRewardRequest, ClaimWindow } from 'types/claim';
import { AccountType, DistributionEvent } from 'types/distribution';

interface DepositRequest {
	contract: string;
	address: string;
	token?: string;
	amount: string;
	domain?: string;
	eventType: 'withdraw' | 'deposit' | 'claim';
}

interface DepositResponse {
	success: boolean;
	message: string;
}

const apiClient = axios.create({
	baseURL: 'https://apis.vcred.trade',
	headers: {
		'Content-Type': 'application/json',
	},
});

export interface UserDepositParams {
	contract: string;
	address: string;
	token?: string;
	amount?: string;
}

interface UserDepositResponse {
	address: string;
	amount: string;
	contract: string;
	createdAt: string;
	id: string;
	updatedAt: string;
}

export interface UserWithdrawDeposit {
	contract: string;
	address: string;
	amount: string;
	token: string;
	eventType: 'withdraw' | 'deposit' | 'claim';
	claimTimePeriod: string;
	claimStatus?: 'processing' | 'processed';
	createdAt?: string;
	domain?: string;
}

export const userDeposit = async (
	depositData: DepositRequest,
): Promise<DepositResponse> => {
	try {
		const response: AxiosResponse<DepositResponse> = await apiClient.post(
			'/api/user/transaction',
			{ ...depositData, domain: window.location.host },
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message ||
				'An error occurred while processing your request.',
		);
	}
};

export const getUserDeposit = async (
	params: UserDepositParams,
): Promise<any> => {
	try {
		const response = await apiClient.get<{
			lastDeposit: UserDepositResponse[];
		}>('/api/user/last-deposit', { params });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getUserEarned = async (address: string): Promise<any> => {
	try {
		const response = await apiClient.get<{
			lastDeposit: UserDepositResponse[];
		}>(`/api/user/dudu/total-earning?user_wallet_addrs=${address}`);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getTotalWallet = async (): Promise<any> => {
	try {
		const response = await apiClient.get<{
			lastDeposit: UserDepositResponse[];
		}>('/api/stats/users-summary');
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const userWithdrawTransaction = async (
	payload: UserWithdrawDeposit,
): Promise<any> => {
	try {
		const response = await apiClient.post<{
			lastDeposit: UserDepositResponse[];
		}>('/api/user/transaction', {
			...payload,
			domain: window.location.host,
		});
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getClaimList = async (contract: string, address: string) => {
	try {
		const response: AxiosResponse<UserWithdrawDeposit[]> =
			await apiClient.get(
				`/api/user/claims?address=${address}&contract=${contract}&domain=${window.location.host}`,
			);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getConfiguration = async (address: string) => {
	try {
		const response: AxiosResponse<
			{
				address: `0x${string}`;
				agent: string;
				config: Configuration;
				isStarted: false;
				status: AgentStates;
				createdAt: string;
				updatedAt: string;
				id: string;
			}[]
		> = await apiClient.get(`/api/agents?addresses=${address}`);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const saveConfiguration = async (value: PostConfiguration) => {
	try {
		const response: AxiosResponse<Configuration> = await apiClient.post(
			`/api/agents/config`,
			value,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data.errors || 'Error fetching user deposit data',
		);
	}
};

export const startAgent = async (agentId: string, address: string) => {
	try {
		const response: AxiosResponse<Configuration> = await apiClient.post(
			`/api/agents/${agentId}/start`,
			{ address },
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const stopAgent = async (agentId: string, address: string) => {
	try {
		const response: AxiosResponse<Configuration> = await apiClient.post(
			`/api/agents/${agentId}/request-stop`,
			{ address },
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getAgent = async (agentId: string) => {
	try {
		const response: AxiosResponse<Configuration> = await apiClient.get(
			`/api/agents/${agentId}`,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getClaimWindow = async (agent: string) => {
	try {
		const response: AxiosResponse<ClaimWindow> = await apiClient.get(
			`/api/reward/claim-window`,
			{
				params: { agent },
			},
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const createNewClaimReward = async (payload: ClaimRewardRequest) => {
	try {
		const response: AxiosResponse<ClaimWindow> = await apiClient.post(
			`/api/reward/claim`,
			payload,
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const listOfAllRewardRequest = async (
	payload: Pick<ClaimRewardRequest, 'domain' | 'contract' | 'address'>,
) => {
	try {
		const response: AxiosResponse<ClaimRewardRequest[]> =
			await apiClient.get(`/api/reward/user-claims`, { params: payload });
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const listOfAllDistribution = async (
	payload: Pick<ClaimRewardRequest, 'contract' | 'address'>,
) => {
	try {
		const response: AxiosResponse<DistributionEvent[]> =
			await apiClient.get(`/api/reward/distribution`, {
				params: payload,
			});
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getUserAccountInformation = async (
	payload: Pick<ClaimRewardRequest, 'contract' | 'address'>,
) => {
	try {
		const response: AxiosResponse<AccountType> = await apiClient.get(
			`/api/reward/account`,
			{
				params: payload,
			},
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};

export const getUserDetails = async () => {
	try {
		const response: AxiosResponse<ClaimRewardRequest[]> = await axios.get(
			`https://api.parsec.finance/graphql`,
			{
				params: {
					operationName: 'GetTxs',
					variables: {
						addresses: [
							'0x271c39c9526e89b5973dcfda6f81c37fc7243990',
						],
						limit: 25,
						offset: 0,
						chains: ['hyper_evm'],
					},
					query: 'query GetTxs($addresses: [String!], $limit: Int!, $chains: [String], $offset: Int, $call: String, $to: String, $from: String, $since: Int, $before: Int) {\n  txs(\n    addresses: $addresses\n    limit: $limit\n    chains: $chains\n    offset: $offset\n    functionCall: $call\n    to: $to\n    from: $from\n    since: $since\n    before: $before\n  ) {\n    chain\n    block\n    hash\n    to\n    toAddressLabel {\n      label\n      src\n      address\n      tags\n      chains\n      imgSrc\n      asset {\n        id\n        address\n        symbol\n        imgSrc\n        chains\n        __typename\n      }\n      __typename\n    }\n    from\n    fromAddressLabel {\n      label\n      src\n      address\n      tags\n      chains\n      imgSrc\n      asset {\n        id\n        address\n        symbol\n        imgSrc\n        chains\n        __typename\n      }\n      __typename\n    }\n    gasPrice\n    gasUsed\n    input\n    call\n    status\n    index\n    value\n    timestamp\n    meta\n    contractCreation\n    marketData {\n      type\n      data\n      __typename\n    }\n    inputDecoded {\n      function\n      args\n      __typename\n    }\n    message\n    __typename\n  }\n}',
				},
			},
		);
		return response.data;
	} catch (error: any) {
		throw new Error(
			error.response?.data?.message || 'Error fetching user deposit data',
		);
	}
};
